/**
 * ItemKinds — the one place that points the itemRegistry façade at the
 * per-kind registries.
 *
 * Both environments call this: the server from `src/server/runtime/registries.js`
 * at boot, the client from `client.js` once its configs are fetched. Keeping a
 * single definition is what makes "client and server agree on ids for all
 * kinds" a property of the code rather than of two lists someone has to keep
 * in sync — and it means adding a kind is one line here, not one per side.
 *
 * The adapters are thin wrappers rather than the registries themselves, so the
 * façade depends on `get`/`all` instead of each registry's own method names.
 * They read the registry live, so this may be called before the registries are
 * populated.
 */

import { itemRegistry } from './ItemRegistry.js';
import { weaponRegistry } from './WeaponRegistry.js';
import { abilityRegistry } from './AbilityRegistry.js';
import { consumableRegistry } from './ConsumableRegistry.js';
import { ammoRegistry } from './AmmoRegistry.js';

/**
 * Register every shipped item kind on the given façade.
 * @param {object} [registry] defaults to the shared singleton
 * @returns {string[]} the kinds now registered
 */
function registerStandardItemKinds(registry = itemRegistry) {
  registry.registerKind('weapon', {
    get: (id) => weaponRegistry.get(id),
    all: () => weaponRegistry.getAll()
  });
  registry.registerKind('ability', {
    get: (id) => abilityRegistry.get(id),
    all: () => abilityRegistry.getAll()
  });
  registry.registerKind('consumable', {
    get: (id) => consumableRegistry.get(id),
    all: () => consumableRegistry.getAll()
  });
  registry.registerKind('ammo', {
    get: (id) => ammoRegistry.get(id),
    all: () => ammoRegistry.getAll()
  });
  return registry.kinds();
}

export { registerStandardItemKinds };
